import React from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string-for-all';
import { getSortedQuery } from '@prisjakt/product-collection-urls';
import type { ProductCollectionSortDirection } from '../../../../../types';
import * as S from './styles';
import { IconDropdownDown } from '@prisjakt/ui/icon/dropdown-down';
import { IconDropdownUp } from '@prisjakt/ui/icon/dropdown-up';

const SORT_ICON_SIZE = 24;
const SORT_DIRECTION_SYMBOLS = {
  asc: <IconDropdownUp size={SORT_ICON_SIZE} />,
  desc: <IconDropdownDown size={SORT_ICON_SIZE} />,
};

const SortHeader = ({
  name,
  sortKey,
  currentSort,
  sortDirection,
}: {
  name: string;
  sortKey: string;
  currentSort: string;
  sortDirection: ProductCollectionSortDirection;
}) => {
  const isSelected = sortKey === currentSort;
  const location = useLocation();
  const queryParamDirection: keyof typeof SORT_DIRECTION_SYMBOLS =
    sortDirection === 'asc' ? 'desc' : 'asc';

  const queryParams = getSortedQuery({
    ...queryString.parse(location.search.replace(/^\?/, '')),
    offset: undefined,
    sort: sortKey,
    direction: isSelected ? queryParamDirection : undefined,
  });
  return (
    <S.ClickableHeader
      to={`${location.pathname}?${queryString.stringify(queryParams)}`}
    >
      <S.HeaderText>
        <S.ColumnName>{name}</S.ColumnName>
        <S.SortIconContainer show={isSelected}>
          {SORT_DIRECTION_SYMBOLS[isSelected ? sortDirection : 'desc']}
        </S.SortIconContainer>
      </S.HeaderText>
    </S.ClickableHeader>
  );
};

export type Column = {
  name: string;
  sortKey?: string;
};

type TableHeadersProps = Omit<
  TableHeaderColumnProps,
  'column' | 'indicateNewColumn'
> & {
  columns: Column[];
  indicateNewColumn: number;
};

export const TableHeader = ({
  columns,
  indicateNewColumn,
  scrolled,
  ...props
}: TableHeadersProps) => {
  return (
    <thead>
      <S.HeaderTr>
        {columns.map((column, index) => {
          return (
            <TableHeaderColumn
              key={column.name}
              {...props}
              column={column}
              indicateNewColumn={indicateNewColumn === index}
              minWidth={index === 0 ? '300px' : undefined}
              scrolled={index === 0 && scrolled}
            />
          );
        })}
      </S.HeaderTr>
    </thead>
  );
};

type TableHeaderColumnProps = {
  column: Column;
  currentSort: string;
  sortDirection: ProductCollectionSortDirection;
  indicateNewColumn: boolean;
  minWidth?: string;
  sticky?: boolean;
  scrolled?: boolean;
  sidebarOpen?: boolean;
};

const TableHeaderColumn = ({
  column,
  currentSort,
  sortDirection,
  indicateNewColumn,
  minWidth,
  sticky,
  scrolled,
  sidebarOpen,
}: TableHeaderColumnProps) => {
  const { name, sortKey } = column;

  return (
    <S.Th
      canBeSorted={!!sortKey}
      indicateNewColumn={indicateNewColumn}
      data-test="ProductListColumnHeader"
      style={{
        minWidth,
      }}
      sticky={sticky}
      scrolled={scrolled}
      sidebarOpen={sidebarOpen}
    >
      {sortKey ? (
        <SortHeader {...{ sortKey, name, currentSort, sortDirection }} />
      ) : (
        <S.HeaderText>
          <S.ColumnName>{name}</S.ColumnName>
        </S.HeaderText>
      )}
    </S.Th>
  );
};
